import React from "react";

import ButtonsGroup from "components/ButtonsGroup";
import CommentField from "components/CommentField";
import CustomSelect from "components/CustomSelect";
import { useExamLockerContext } from "components/ExamLockerContext/ExamLockerContext";
import {
  createCustomOption,
  CUSTOM_VALUE,
  getFieldType,
  mapButtons,
  mapOptions
} from "../helper";
import MediaBasket from "../MediaBasket";
import { useVetExamContext } from "../VetExamProvider/VetExamProvider";
import {
  EFieldsTypes,
  EVetExamFields,
  FormField,
  IExamDetail,
  IExamDetailObservation,
  IExamJog
} from "../options";
import useStyles from "./styles";

interface IProps {
  field: FormField;
  entryState: IExamJog | IExamDetail | IExamDetailObservation;
  parentEntityId?: string;
}

const getDataTestLabel = (key: EVetExamFields) => {
  switch (key) {
    case EVetExamFields.DETAIL_LIMB: {
      return "examDetailsLimbDropdown";
    }
    case EVetExamFields.DETAIL_LOCATION: {
      return "examDetailsLocationDropdown";
    }
    case EVetExamFields.DETAIL_STRUCTURE: {
      return "examDetailsStructDropdown";
    }
    case EVetExamFields.DETAIL_OBSERVATION: {
      return "examDetailsObservationDropdown";
    }
    case EVetExamFields.DETAIL_CONDITION: {
      return "examDetailsConditionDropdown";
    }
    case EVetExamFields.DETAIL_NOTE: {
      return "examDetailsConditionNote";
    }
  }
};

const VetExamFormField = (props: IProps) => {
  const { field, entryState, parentEntityId } = props;

  const classes = useStyles();
  const { lockExam } = useExamLockerContext();
  const { values, errors, setFieldValue, formFieldsItems } =
    useVetExamContext();
  const { examJogs, examDetails } = values;

  const { isJogField, isDetailField, isObservationField } = getFieldType(
    field.key
  );
  const validationSection = isJogField
    ? values[EVetExamFields.EXAM_JOGS]
    : values[EVetExamFields.EXAM_DETAILS];
  const errorsSection = isJogField
    ? errors[EVetExamFields.EXAM_JOGS]
    : errors[EVetExamFields.EXAM_DETAILS];
  let currentEntityIndex: number;
  let parentEntityIndex: number;
  let errorMessage: string;

  if (isObservationField && parentEntityId) {
    parentEntityIndex = validationSection.findIndex(
      item => item.id === parentEntityId
    );

    currentEntityIndex = validationSection[parentEntityIndex]?.[
      EVetExamFields.EXAM_OBSERVATIONS
    ].findIndex(item => item.id === entryState.id);

    errorMessage =
      errorsSection?.[parentEntityIndex]?.[EVetExamFields.EXAM_OBSERVATIONS]?.[
        currentEntityIndex
      ]?.[field.key];
  } else if (isJogField || isDetailField) {
    currentEntityIndex = validationSection.findIndex(
      item => item.id === entryState.id
    );

    errorMessage = errorsSection?.[currentEntityIndex]?.[field.key];
  }

  const customNameField = entryState[field.custom];
  const options = mapOptions(formFieldsItems[field.optionsName]);

  const changeFieldValue = (value: string, customValue: string = null) => {
    const { isJogField, isDetailField, isObservationField } = getFieldType(
      field.key
    );

    if (isJogField) {
      setFieldValue(
        EVetExamFields.EXAM_JOGS,
        examJogs.map((jog: IExamJog) => {
          if (jog.id !== entryState.id) {
            return jog;
          }

          const updatedJog = { ...jog, [field.key]: value };

          if (customValue) {
            updatedJog[field.custom] = customValue;
          }

          return updatedJog;
        })
      );
    }
    if (isDetailField) {
      setFieldValue(
        EVetExamFields.EXAM_DETAILS,
        examDetails.map((detail: IExamDetail) => {
          if (detail.id !== entryState.id) {
            return detail;
          }

          const updatedDetails = { ...detail, [field.key]: value };

          if (customValue) {
            updatedDetails[field.custom] = customValue;
          }

          return updatedDetails;
        })
      );
    }
    if (isObservationField && parentEntityId) {
      setFieldValue(
        EVetExamFields.EXAM_DETAILS,
        examDetails.map((detail: IExamDetail) => {
          if (detail.id === parentEntityId) {
            return {
              ...detail,
              observationEntries: detail.observationEntries.map(item => {
                if (item.id !== entryState.id) {
                  return item;
                }

                const updatedObservation = {
                  ...item,
                  [field.key]: value
                };

                if (customValue) {
                  updatedObservation[field.custom] = customValue;
                }

                return updatedObservation;
              })
            };
          }

          return detail;
        })
      );
    }

    lockExam();
  };

  const handleButtonClick = (value: string) => {
    changeFieldValue(value);
  };

  const handleChangeSelect = (value: string, customValue: string) => {
    changeFieldValue(value, customValue);
  };

  switch (field.type) {
    case EFieldsTypes.BUTTONS_GROUP: {
      return (
        <ButtonsGroup
          key={field.key}
          className={classes.buttonGroup}
          title={field.title}
          name={field.key}
          buttons={mapButtons(formFieldsItems[field.optionsName], field.key)}
          selectedValue={entryState[field.key]}
          onClickButton={handleButtonClick}
          errorMessage={errorMessage}
        />
      );
    }
    case EFieldsTypes.SELECT: {
      if (entryState[field.key] === CUSTOM_VALUE) {
        options.push(createCustomOption(customNameField));
      }

      return (
        <CustomSelect
          key={field.key}
          className={classes.select}
          hasCustomField={true}
          options={options}
          label={field.title}
          name={field.key}
          inputLabel={field.title}
          selectedValue={entryState[field.key]}
          onChangeSelectedValue={handleChangeSelect}
          dataTestLabel={getDataTestLabel(field.key)}
          errorMessage={errorMessage}
        />
      );
    }
    case EFieldsTypes.TEXT: {
      return (
        <CommentField
          key={field.key}
          title={field.title}
          className={classes.examComment}
          textareaClassName={classes.textareaField}
          onChange={changeFieldValue}
          defaultValue={entryState[field.key] || ""}
          data-test-id={getDataTestLabel(field.key)}
        />
      );
    }
    case EFieldsTypes.MEDIA: {
      return <MediaBasket key={field.key} />;
    }
  }

  return <></>;
};

export default VetExamFormField;
